import React, { useState, useEffect } from 'react';
import { useNavigation } from '../context/NavigationContext';
import { useAuth } from '../../context/AuthContext';
import { getTurkishAuthErrorMessage } from '../../services/firebaseAuthService';
import { IndustryType, SubscriptionPlanId } from '../../types';
import { SectorCategory } from '../types/publicTypes';
import {
  Sparkles,
  Lock,
  Mail,
  User,
  Building2,
  Anchor,
  Scale,
  Stethoscope,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Zap,
} from 'lucide-react';

export const RegisterPage: React.FC = () => {
  const { navigate } = useNavigation();
  const { registerWithEmail, loginWithGoogle, isAuthenticated, currentUser } = useAuth();

  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    companyName: '',
    sector: 'CORPORATE' as SectorCategory,
    subdomain: '',
  });

  // If already authenticated, redirect directly to dashboard
  useEffect(() => {
    if (isAuthenticated && currentUser) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, currentUser, navigate]);

  const handleNextStep = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.password || !formData.companyName.trim()) {
      setError('Lütfen tüm zorunlu alanları doldurunuz.');
      return;
    }
    if (formData.password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }

    // Auto-generate subdomain suggestion
    const slug = formData.companyName
      .toLowerCase()
      .trim()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    setFormData((prev) => ({ ...prev, subdomain: slug || 'benim-sitem' }));
    setStep(2);
  };

  const handleFinalSubmit = async () => {
    setError(null);
    setLoading(true);

    try {
      await registerWithEmail(
        formData.email,
        formData.password,
        formData.fullName,
        formData.companyName,
        formData.sector
      );
      // Requirement: Direct redirect to main dashboard on register, without wizard/create site force
      navigate('/dashboard');
    } catch (err: any) {
      setError(getTurkishAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
      // Requirement: Direct redirect to main dashboard on register
      navigate('/dashboard');
    } catch (err: any) {
      setError(getTurkishAuthErrorMessage(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-center py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-600/15 blur-[140px] pointer-events-none" />

      <div className="sm:mx-auto sm:w-full sm:max-w-lg relative z-10">
        {/* Brand Header */}
        <div
          id="register-brand-logo-btn"
          onClick={() => navigate('/')}
          className="flex items-center justify-center gap-3 cursor-pointer group mb-6"
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 via-indigo-600 to-sky-400 flex items-center justify-center shadow-md shadow-blue-600/30 group-hover:scale-105 transition-transform">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-2xl font-bold tracking-tight text-white font-sans">
              Adio<span className="text-blue-400">soft</span>
            </span>
            <span className="text-[10px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
              Builder
            </span>
          </div>
        </div>

        <h2 className="text-center text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
          14 Günlük Ücretsiz Denemenizi Başlatın
        </h2>
        <p className="mt-2 text-center text-xs sm:text-sm text-slate-400">
          Kredi kartı gerekmez. Dakikalar içinde kendi sitenizi oluşturun.
        </p>

        {/* Step Indicator */}
        {!success && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <div
              className={`flex items-center gap-2 text-xs font-semibold px-3 py-1 rounded-full ${
                step === 1
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-900 text-slate-400 border border-slate-800'
              }`}
            >
              <span>1</span>
              <span>Hesap Bilgileri</span>
            </div>
            <div className="w-4 h-[1px] bg-slate-800" />
            <div
              className={`flex items-center gap-2 text-xs font-semibold px-3 py-1 rounded-full ${
                step === 2
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-900 text-slate-400 border border-slate-800'
              }`}
            >
              <span>2</span>
              <span>Sektör & Şablon</span>
            </div>
          </div>
        )}
      </div>

      {/* Main Registration Card */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg relative z-10">
        <div className="bg-slate-900/90 border border-slate-800 py-8 px-6 sm:px-8 rounded-3xl shadow-2xl">
          {success ? (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-white">Hesabınız & Siteniz Hazırlandı!</h3>
              <p className="text-xs sm:text-sm text-slate-300 max-w-sm mx-auto">
                <strong className="text-white">{formData.companyName || 'İşletmeniz'}</strong> için güvenli bulut altyapımız üzerinde kullanıcı hesabı ve sektörel alan oluşturuldu.
              </p>
              <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 text-xs font-mono text-blue-400">
                https://www.adiosoft.com/{formData.subdomain || 'site'}
              </div>
              <p className="text-[11px] text-slate-400">
                Doğrulama bağlantısı e-posta adresinize gönderildi. Yönetici paneline geçip sitenizi özelleştirmeye başlayabilirsiniz.
              </p>
              <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  id="go-to-dashboard-btn"
                  onClick={() => navigate('/dashboard')}
                  className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs sm:text-sm font-bold transition shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2"
                >
                  <span>Yönetici Paneline Git</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : step === 1 ? (
            <div className="space-y-4">
              {/* Google Sign-up Button */}
              <button
                id="google-register-btn"
                type="button"
                disabled={googleLoading || loading}
                onClick={handleGoogleSignUp}
                className="w-full py-3 px-4 rounded-xl bg-slate-950 hover:bg-slate-800 border border-slate-700 text-slate-200 font-semibold text-xs sm:text-sm flex items-center justify-center gap-3 transition shadow-sm disabled:opacity-50"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                  />
                </svg>
                <span>{googleLoading ? 'Google Bağlantısı...' : 'Google ile Hızlı Kayıt Ol'}</span>
              </button>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-[1px] bg-slate-800" />
                <span className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">veya E-Posta ile</span>
                <div className="flex-1 h-[1px] bg-slate-800" />
              </div>

              {error && (
                <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-xs flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{error}</span>
                </div>
              )}

              <form onSubmit={handleNextStep} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    Adınız ve Soyadınız *
                  </label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      id="register-fullname-input"
                      type="text"
                      required
                      value={formData.fullName}
                      onChange={(e) =>
                        setFormData({ ...formData, fullName: e.target.value })
                      }
                      placeholder="Ahmet Yılmaz"
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    Şirket veya İşletme Adınız *
                  </label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      id="register-company-input"
                      type="text"
                      required
                      value={formData.companyName}
                      onChange={(e) =>
                        setFormData({ ...formData, companyName: e.target.value })
                      }
                      placeholder="Örn. Apex Yachting & Travel"
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    E-Posta Adresiniz *
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      id="register-email-input"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      placeholder="ahmet@sirketiniz.com"
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">
                    Şifre Belirleyin *
                  </label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      id="register-password-input"
                      type="password"
                      required
                      minLength={6}
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      placeholder="En az 6 karakter"
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    id="register-step1-next-btn"
                    type="submit"
                    className="w-full py-3.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 transition active:scale-95"
                  >
                    <span>Devam Et (Sektör Seçimi)</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <h4 className="text-sm font-bold text-white mb-1">
                  Web Sitenizin Sektörünü Belirleyin
                </h4>
                <p className="text-xs text-slate-400">
                  Seçtiğiniz sektöre göre hazır modüller ve görsel editör şablonları yüklenecektir.
                </p>
              </div>

              {error && (
                <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-xs flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{error}</span>
                </div>
              )}

              {/* Sector Option Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div
                  id="sector-option-corporate"
                  onClick={() =>
                    setFormData({ ...formData, sector: 'CORPORATE' })
                  }
                  className={`p-3.5 rounded-2xl border cursor-pointer transition ${
                    formData.sector === 'CORPORATE'
                      ? 'bg-blue-600/20 border-blue-500 text-white shadow-lg shadow-blue-600/15'
                      : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  <Building2 className="w-5 h-5 text-blue-400 mb-2" />
                  <h5 className="text-xs font-bold">Kurumsal & Holding</h5>
                  <p className="text-[10px] text-slate-500 mt-0.5">Hizmet vitrini, Blog & İletişim</p>
                </div>

                <div
                  id="sector-option-yacht"
                  onClick={() =>
                    setFormData({ ...formData, sector: 'YACHT_RENTAL' })
                  }
                  className={`p-3.5 rounded-2xl border cursor-pointer transition ${
                    formData.sector === 'YACHT_RENTAL'
                      ? 'bg-blue-600/20 border-blue-500 text-white shadow-lg shadow-blue-600/15'
                      : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  <Anchor className="w-5 h-5 text-sky-400 mb-2" />
                  <h5 className="text-xs font-bold">Lüks Yat Kiralama</h5>
                  <p className="text-[10px] text-slate-500 mt-0.5">Filo, Takvim & Rezervasyon</p>
                </div>

                <div
                  id="sector-option-legal"
                  onClick={() =>
                    setFormData({ ...formData, sector: 'LEGAL' })
                  }
                  className={`p-3.5 rounded-2xl border cursor-pointer transition ${
                    formData.sector === 'LEGAL'
                      ? 'bg-blue-600/20 border-blue-500 text-white shadow-lg shadow-blue-600/15'
                      : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  <Scale className="w-5 h-5 text-amber-400 mb-2" />
                  <h5 className="text-xs font-bold">Hukuk & Avukatlık</h5>
                  <p className="text-[10px] text-slate-500 mt-0.5">Danışmanlık formu & Makaleler</p>
                </div>

                <div
                  id="sector-option-health"
                  onClick={() =>
                    setFormData({ ...formData, sector: 'HEALTH' })
                  }
                  className={`p-3.5 rounded-2xl border cursor-pointer transition ${
                    formData.sector === 'HEALTH'
                      ? 'bg-blue-600/20 border-blue-500 text-white shadow-lg shadow-blue-600/15'
                      : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  <Stethoscope className="w-5 h-5 text-emerald-400 mb-2" />
                  <h5 className="text-xs font-bold">Sağlık & Klinik</h5>
                  <p className="text-[10px] text-slate-500 mt-0.5">Randevu & Tedavi Bilgisi</p>
                </div>
              </div>

              {/* URL Slug Preview */}
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  Ücretsiz Adiosoft Web Adresiniz
                </label>
                <div className="flex items-center rounded-xl bg-slate-950 border border-slate-800 px-3 py-2 text-xs font-mono">
                  <span className="text-slate-500 mr-1 select-none">adiosoft.com/</span>
                  <input
                    id="register-subdomain-input"
                    type="text"
                    value={formData.subdomain}
                    onChange={(e) =>
                      setFormData({ ...formData, subdomain: e.target.value })
                    }
                    className="bg-transparent text-white focus:outline-none flex-1 font-mono"
                  />
                </div>
                <span className="text-[10px] text-slate-500 mt-1 block">
                  Daha sonra kendi özel alan adınızı (www.sirketiniz.com) bağlayabilirsiniz.
                </span>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-1/3 py-3 rounded-xl bg-slate-950 border border-slate-800 text-slate-300 text-xs font-semibold hover:bg-slate-900 transition"
                >
                  Geri
                </button>
                <button
                  id="register-final-submit-btn"
                  type="button"
                  disabled={loading}
                  onClick={handleFinalSubmit}
                  className="w-2/3 py-3.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 transition active:scale-95 disabled:opacity-50"
                >
                  {loading ? (
                    <span>Kullanıcı Kaydı Yapılıyor...</span>
                  ) : (
                    <>
                      <span>Kaydı Tamamla ve Panele Git</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Bottom login link */}
          <div className="mt-6 pt-4 border-t border-slate-800/80 text-center text-xs text-slate-400">
            Zaten bir hesabınız var mı?{' '}
            <button
              id="register-go-to-login-btn"
              onClick={() => navigate('/giris')}
              className="text-blue-400 hover:underline font-semibold"
            >
              Giriş Yap
            </button>
          </div>
        </div>

        {/* Security assurance */}
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="w-4 h-4 text-slate-600" />
          <span>14 Gün Deneme — İstediğiniz Zaman İptal Edin</span>
        </div>
      </div>
    </div>
  );
};
